import { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import api, { formatApiError } from "@/lib/api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Loader2, ArrowRight } from "lucide-react";
import { BremaLogo } from "@/components/BremaLogo";
import { useI18n } from "@/i18n";
import { LangSwitcher } from "@/components/LangSwitcher";

const HERO = "https://customer-assets-rejwkqb3.emergentagent.net/job_brema-ai-sales/artifacts/xwxqayi0_foto_3%20%281%29%20copia.webp";

export default function Login() {
  const { user, login, register } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  const [rEmail, setREmail] = useState("");
  const [rPassword, setRPassword] = useState("");
  const [rName, setRName] = useState("");
  const [rShop, setRShop] = useState("");
  const [rVat, setRVat] = useState("");
  const [rPhone, setRPhone] = useState("");
  const [rAddress, setRAddress] = useState("");
  const [rZip, setRZip] = useState("");
  const [rCity, setRCity] = useState("");
  const [rProvince, setRProvince] = useState("");
  const [rCountry, setRCountry] = useState("Italia");
  const [rWebsite, setRWebsite] = useState("");
  const [rNote, setRNote] = useState("");

  const [forgotOpen, setForgotOpen] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotLoading, setForgotLoading] = useState(false);

  const doForgot = async (e) => {
    e.preventDefault();
    setForgotLoading(true);
    try {
      await api.post("/auth/forgot-password", { email: forgotEmail });
    } catch (_) { /* ignore, generic response */ }
    setForgotLoading(false);
    toast.success(t("login.forgot_sent"));
    setForgotOpen(false);
    setForgotEmail("");
  };

  if (user) return <Navigate to="/" replace />;

  const doLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    const res = await login(email, password);
    setLoading(false);
    if (res.ok) {
      toast.success(t("toast.welcome", { name: res.user.name }));
      navigate("/");
    } else toast.error(res.error);
  };

  const doRegister = async (e) => {
    e.preventDefault();
    setLoading(true);
    const res = await register({
      email: rEmail, password: rPassword, name: rName, role: "reseller",
      shop_name: rShop, company_name: rShop,
      vat_number: rVat, phone: rPhone,
      address: rAddress, zip: rZip, city: rCity, province: rProvince, country: rCountry,
      website: rWebsite, note: rNote,
    });
    setLoading(false);
    if (res.ok) {
      toast.success(t("toast.account_created"));
      navigate("/");
    } else toast.error(res.error);
  };

  const fillDemo = (type) => {
    if (type === "admin") { setEmail("admin@bremabike.com"); setPassword("Brema2026!"); }
    else { setEmail("negozio@bikeshop.it"); setPassword("Negozio2026!"); }
  };

  return (
    <div className="grid min-h-screen lg:grid-cols-2">
      {/* Left brand panel */}
      <div className="relative hidden overflow-hidden lg:block">
        <img src={HERO} alt="Brema Bike" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#131822] via-[#131822]/70 to-[#131822]/40" />
        <div className="relative z-10 flex h-full flex-col justify-between p-12">
          <BremaLogo imgClassName="h-24 lg:h-28" />
          <div>
            <p className="mb-3 text-xs font-semibold uppercase tracking-[0.3em] text-brema-yellow">
              {t("login.hero_eyebrow")}
            </p>
            <h1 className="max-w-md text-4xl font-bold leading-tight tracking-tight text-white lg:text-5xl">
              {t("login.hero_title")}
            </h1>
            <p className="mt-4 max-w-md text-slate-200">
              {t("login.hero_sub")}
            </p>
          </div>
        </div>
      </div>

      {/* Right form */}
      <div className="flex items-center justify-center bg-background px-5 py-12">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="w-full max-w-md"
        >
          <div className="mb-8 flex items-center justify-between lg:hidden"><BremaLogo imgClassName="h-16" /></div>
          <div className="mb-4 flex justify-end"><LangSwitcher /></div>

          <Tabs defaultValue="login">
            <TabsList className="grid w-full grid-cols-2 bg-brema-card">
              <TabsTrigger value="login" data-testid="tab-login">{t("login.tab_login")}</TabsTrigger>
              <TabsTrigger value="register" data-testid="tab-register">{t("login.tab_register")}</TabsTrigger>
            </TabsList>

            <TabsContent value="login">
              <div className="rounded-2xl border border-border bg-brema-card p-7">
                <h2 className="text-2xl font-semibold tracking-tight text-foreground">{t("login.welcome_back")}</h2>
                <p className="mb-6 mt-1 text-sm text-muted-foreground">{t("login.login_sub")}</p>
                <form onSubmit={doLogin} className="space-y-4">
                  <div className="space-y-1.5">
                    <Label htmlFor="email">{t("login.email")}</Label>
                    <Input id="email" type="email" required value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      data-testid="login-email" placeholder="tu@negozio.it" />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="password">{t("login.password")}</Label>
                    <Input id="password" type="password" required value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      data-testid="login-password" placeholder="••••••••" />
                  </div>
                  <Button type="submit" disabled={loading} data-testid="login-submit"
                    className="w-full rounded-full bg-primary font-semibold text-primary-foreground hover:bg-primary/90">
                    {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <>{t("login.signin")} <ArrowRight className="ml-1 h-4 w-4" /></>}
                  </Button>
                </form>
                <button type="button" onClick={() => setForgotOpen(true)} data-testid="forgot-password-link"
                  className="mt-3 text-sm text-brand transition-colors hover:underline">{t("login.forgot")}</button>
                <div className="mt-6 rounded-lg border border-border bg-brema-bg p-3">
                  <p className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{t("login.demo")}</p>
                  <div className="flex gap-2">
                    <Button type="button" variant="secondary" size="sm" data-testid="demo-admin"
                      onClick={() => fillDemo("admin")}
                      className="flex-1 rounded-full border border-border bg-transparent text-xs hover:bg-brema-hover">
                      {t("login.demo_admin")}
                    </Button>
                    <Button type="button" variant="secondary" size="sm" data-testid="demo-reseller"
                      onClick={() => fillDemo("reseller")}
                      className="flex-1 rounded-full border border-border bg-transparent text-xs hover:bg-brema-hover">
                      {t("login.demo_reseller")}
                    </Button>
                  </div>
                </div>
              </div>
            </TabsContent>

            <TabsContent value="register">
              <div className="max-h-[78vh] overflow-y-auto rounded-2xl border border-border bg-brema-card p-7">
                <h2 className="text-2xl font-semibold tracking-tight text-foreground">{t("login.register_title")}</h2>
                <p className="mb-6 mt-1 text-sm text-muted-foreground">{t("login.register_sub")}</p>
                <form onSubmit={doRegister} className="space-y-4">
                  <div className="space-y-1.5">
                    <Label htmlFor="rshop">{t("login.shop")} *</Label>
                    <Input id="rshop" required value={rShop} onChange={(e) => setRShop(e.target.value)}
                      data-testid="reg-shop" placeholder="CicloSport Milano S.r.l." />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1.5">
                      <Label htmlFor="rvat">{t("login.vat")} *</Label>
                      <Input id="rvat" required value={rVat} onChange={(e) => setRVat(e.target.value)}
                        data-testid="reg-vat" placeholder="IT01234567890" />
                    </div>
                    <div className="space-y-1.5">
                      <Label htmlFor="rphone">{t("login.phone")} *</Label>
                      <Input id="rphone" required value={rPhone} onChange={(e) => setRPhone(e.target.value)}
                        data-testid="reg-phone" placeholder="+39 02 1234567" />
                    </div>
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="rname">{t("login.ref")} *</Label>
                    <Input id="rname" required value={rName} onChange={(e) => setRName(e.target.value)}
                      data-testid="reg-name" placeholder="Mario Rossi" />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="raddress">{t("login.address")} *</Label>
                    <Input id="raddress" required value={rAddress} onChange={(e) => setRAddress(e.target.value)}
                      data-testid="reg-address" placeholder="Via Torino 45" />
                  </div>
                  <div className="grid grid-cols-3 gap-3">
                    <div className="space-y-1.5">
                      <Label htmlFor="rzip">{t("login.zip")} *</Label>
                      <Input id="rzip" required value={rZip} onChange={(e) => setRZip(e.target.value)}
                        data-testid="reg-zip" placeholder="20123" />
                    </div>
                    <div className="space-y-1.5">
                      <Label htmlFor="rcity">{t("login.city")} *</Label>
                      <Input id="rcity" required value={rCity} onChange={(e) => setRCity(e.target.value)}
                        data-testid="reg-city" placeholder="Milano" />
                    </div>
                    <div className="space-y-1.5">
                      <Label htmlFor="rprovince">{t("login.province")}</Label>
                      <Input id="rprovince" value={rProvince} onChange={(e) => setRProvince(e.target.value)}
                        data-testid="reg-province" placeholder="MI" />
                    </div>
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="rcountry">{t("login.country")} *</Label>
                    <Input id="rcountry" required value={rCountry} onChange={(e) => setRCountry(e.target.value)}
                      data-testid="reg-country" placeholder="Italia" />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="rwebsite">{t("login.website")}</Label>
                    <Input id="rwebsite" value={rWebsite} onChange={(e) => setRWebsite(e.target.value)}
                      data-testid="reg-website" placeholder="https://www.ilmionegozio.it" />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="remail">{t("login.email")} *</Label>
                    <Input id="remail" type="email" required value={rEmail} onChange={(e) => setREmail(e.target.value)}
                      data-testid="reg-email" placeholder="tu@negozio.it" />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="rpass">{t("login.password")} *</Label>
                    <Input id="rpass" type="password" required value={rPassword} onChange={(e) => setRPassword(e.target.value)}
                      data-testid="reg-password" placeholder={t("login.password_ph")} />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="rnote">{t("login.notes")}</Label>
                    <Textarea id="rnote" rows={3} value={rNote} onChange={(e) => setRNote(e.target.value)}
                      data-testid="reg-note" placeholder="…" />
                  </div>
                  <Button type="submit" disabled={loading} data-testid="register-submit"
                    className="w-full rounded-full bg-primary font-semibold text-primary-foreground hover:bg-primary/90">
                    {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : t("login.create")}
                  </Button>
                </form>
              </div>
            </TabsContent>
          </Tabs>
        </motion.div>
      </div>

      <Dialog open={forgotOpen} onOpenChange={setForgotOpen}>
        <DialogContent className="max-w-md border-border bg-brema-card" data-testid="forgot-password-dialog">
          <DialogHeader>
            <DialogTitle className="text-foreground">{t("login.forgot_title")}</DialogTitle>
            <DialogDescription>{t("login.forgot_desc")}</DialogDescription>
          </DialogHeader>
          <form onSubmit={doForgot} className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="forgot-email">{t("login.email")}</Label>
              <Input id="forgot-email" type="email" required value={forgotEmail}
                onChange={(e) => setForgotEmail(e.target.value)}
                data-testid="forgot-email" placeholder="tu@negozio.it" />
            </div>
            <DialogFooter>
              <Button type="submit" disabled={forgotLoading} data-testid="forgot-submit"
                className="w-full rounded-full bg-primary font-semibold text-primary-foreground hover:bg-primary/90">
                {forgotLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : t("login.forgot_send")}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
